import { useState, useEffect } from 'react';
import { Save } from 'lucide-react';

import {
  Dialog,
  DialogContent,
  DialogTitle,
} from '../ui/dialog';
import { Button } from '../ui/button';
import useLocalStorage from '../../hooks/useLocalStorage';

import SplitViewEditor from './SplitViewEditor';


/**
 * CodeEditorModal Component
 *
 * Full-screen modal wrapper for the split-view code editor.
 * Features:
 * - 95vw × 95vh size
 * - Auto-save to localStorage
 * - Unsaved changes warning
 * - Escape key handling
 */
const CodeEditorModal = ({ open, onOpenChange, value, onChange, pageId }) => {
  const [localContent, setLocalContent] = useState(value || '');
  const [isDirty, setIsDirty] = useState(false);
  const [showDraftBanner, setShowDraftBanner] = useState(false);
  const [draftTimestamp, setDraftTimestamp] = useState(null);

  // Auto-save draft to localStorage (2 second debounce)
  const draftKey = `wysiwyg-draft-${pageId || 'new'}`;
  const [draft, setDraft, clearDraft] = useLocalStorage(
    draftKey,
    { content: '', timestamp: null, pageId: pageId || 'new' },
    2000 // 2 second debounce
  );

  // Track if we've initialized for this open session
  const [initialized, setInitialized] = useState(false);

  // Initialize content from draft or prop - only run when modal opens
  useEffect(() => {
    if (!open) {
      setInitialized(false);
      return;
    }

    if (initialized) return;

    // Check for existing draft (only on initial open)
    if (draft && draft.content && draft.content !== value) {
      // Found a draft that differs from current value
      setLocalContent(draft.content);
      setDraftTimestamp(draft.timestamp);
      setShowDraftBanner(true);
      setIsDirty(true);
    } else {
      // No draft or draft matches value, use prop value
      setLocalContent(value || '');
      setIsDirty(false);
      setShowDraftBanner(false);
    }
    setInitialized(true);
  }, [open, initialized, draft, value]);

  // Update draft when content changes (separate from initialization)
  useEffect(() => {
    if (!open || !initialized) return;

    const timer = setTimeout(() => {
      setDraft({
        content: localContent,
        timestamp: Date.now(),
        pageId: pageId || 'new'
      });
    }, 100);

    // Mark as dirty if content changed from original value
    setIsDirty(localContent !== value);

    return () => clearTimeout(timer);
  }, [localContent, open, initialized, setDraft, pageId, value]);

  // Handle content change from editor
  const handleContentChange = (newContent) => {
    setLocalContent(newContent);
  };

  // Handle save
  const handleSave = () => {
    onChange(localContent);
    clearDraft();
    setIsDirty(false);
    onOpenChange(false);
  };

  // Handle cancel
  const handleCancel = () => {
    if (isDirty) {
      const confirmed = window.confirm(
        'You have unsaved changes. Are you sure you want to close without saving?'
      );
      if (!confirmed) return;
    }

    // Revert to original value
    setLocalContent(value || '');
    onOpenChange(false);
  };

  // Handle Escape key
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape' && open) {
        e.preventDefault();
        if (isDirty) {
          const confirmed = window.confirm(
            'You have unsaved changes. Are you sure you want to close without saving?'
          );
          if (!confirmed) return;
        }
        setLocalContent(value || '');
        onOpenChange(false);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [open, isDirty, value, onOpenChange]);

  // Prevent body scroll when modal is open
  useEffect(() => {
    if (open) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }

    return () => {
      document.body.style.overflow = '';
    };
  }, [open]);

  // Format timestamp for display
  const formatTimestamp = (timestamp) => {
    if (!timestamp) return '';

    const date = new Date(timestamp);
    const now = new Date();
    const diffSeconds = Math.floor((now - date) / 1000);

    if (diffSeconds < 60) return `${diffSeconds} seconds ago`;
    if (diffSeconds < 3600) return `${Math.floor(diffSeconds / 60)} minutes ago`;
    if (diffSeconds < 86400) return `${Math.floor(diffSeconds / 3600)} hours ago`;

    return date.toLocaleDateString() + ' ' + date.toLocaleTimeString();
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-[95vw] h-[95vh] p-0 flex flex-col">
        {/* Header */}
        <div className="border-b p-4 flex-shrink-0">
          <div className="flex items-center justify-between pr-8">
            <DialogTitle className="text-xl font-semibold">
              Edit Page Content
            </DialogTitle>
            {isDirty && (
              <span className="text-sm text-orange-600 font-medium">
                ● Unsaved changes
                {draftTimestamp && ` (${formatTimestamp(draftTimestamp)})`}
              </span>
            )}
          </div>

          {/* Draft Restored Banner */}
          {showDraftBanner && (
            <div className="mt-3 bg-blue-50 dark:bg-blue-900/30 border border-blue-200 dark:border-blue-800 text-blue-700 dark:text-blue-300 px-4 py-2 rounded text-sm flex items-center justify-between">
              <span>
                Draft restored from {formatTimestamp(draftTimestamp)}
              </span>
              <Button
                size="sm"
                variant="ghost"
                onClick={() => {
                  setShowDraftBanner(false);
                  setLocalContent(value || '');
                  clearDraft();
                  setIsDirty(false);
                }}
                className="text-blue-700 dark:text-blue-300 hover:text-blue-800 dark:hover:text-blue-200"
              >
                Discard Draft
              </Button>
            </div>
          )}
        </div>

        {/* Editor Area */}
        <div className="flex-1 overflow-hidden">
          <SplitViewEditor
            value={localContent}
            onChange={handleContentChange}
            isDirty={isDirty}
            onFormat={() => {
              // Optional: show toast notification
            }}
          />
        </div>

        {/* Footer */}
        <div className="border-t p-4 flex-shrink-0">
          <div className="flex justify-end space-x-2">
            <Button variant="outline" onClick={handleCancel}>
              Cancel
            </Button>
            <Button
              onClick={handleSave}
              variant="adminPrimary"
            >
              <Save className="h-4 w-4 mr-2" />
              Save Changes
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default CodeEditorModal;
